import {
  ProjectMailAliasStatus,
  ProjectMailItemSummary,
  ProjectMailService,
} from '@beeboard/common';
import { defineStore } from 'pinia';

type ProjectMailStoreState = {
  /** Alias status per project id. */
  aliasStatus: Record<string, ProjectMailAliasStatus>;
  /** Mail per project id, as offered by the import sidebar. */
  items: Record<string, ProjectMailItemSummary[]>;
  /** How many mails the server has for the project, so the UI knows more exist. */
  totals: Record<string, number>;
  /** Pages currently loaded per project — what a refresh has to restore. */
  loadedPages: Record<string, number>;
  isLoading: boolean;
  isLoadingMore: boolean;
};

/** Rows per request, and per press of "load more". */
const PAGE_SIZE = 100;

/** Ceiling on the pages a refresh restores; only reachable after 100 "load more" presses. */
const MAX_PAGES = 100;

/**
 * In-flight loads keyed by project, so concurrent callers share one paging run.
 * Module scope, not store state: promises have no business being reactive.
 */
const loadsInFlight = new Map<string, Promise<void>>();

/**
 * Project mail (WI #5643) — alias configuration plus the mail the map's import sidebar
 * offers as a drag source. Keyed by project id, because the map can have several
 * projects open and the sidebar reads whichever is active.
 */
export const useProjectMailStore = defineStore('projectMailStore', {
  state: (): ProjectMailStoreState => ({
    aliasStatus: {},
    items: {},
    totals: {},
    loadedPages: {},
    isLoading: false,
    isLoadingMore: false,
  }),

  getters: {
    status: (state) => (projectId: string) => state.aliasStatus[projectId],

    isEnabled: (state) => (projectId: string) =>
      !!state.aliasStatus[projectId]?.enabled,

    address: (state) => (projectId: string) =>
      state.aliasStatus[projectId]?.alias?.address || '',

    mailItems: (state) => (projectId: string) => state.items[projectId] || [],

    /** Total the server reports, which is more than is loaded until the last page. */
    mailItemsTotal: (state) => (projectId: string) =>
      state.totals[projectId] ?? (state.items[projectId] || []).length,

    hasMoreMailItems: (state) => (projectId: string) =>
      (state.items[projectId] || []).length < (state.totals[projectId] ?? 0),
  },

  actions: {
    /** Loads the alias status once per project; safe to call from anywhere. */
    async ensureAliasStatus(projectId: string) {
      if (this.aliasStatus[projectId]) {
        return this.aliasStatus[projectId];
      }

      try {
        return await this.loadAliasStatus(projectId);
      } catch {
        // Project mail is optional — a failure here must never break the caller
        // (the import sidebar calls this on every open).
        return undefined;
      }
    },

    async loadAliasStatus(projectId: string) {
      const status = await ProjectMailService.getProjectMailAlias(projectId);
      this.aliasStatus[projectId] = status;

      return status;
    },

    /** Enable or rename. The server rejects a slug reserved by another project. */
    async saveAlias(projectId: string, slug: string) {
      const isEnabled = this.aliasStatus[projectId]?.enabled;

      if (isEnabled) {
        await ProjectMailService.updateProjectMailAlias(projectId, { slug });
      } else {
        await ProjectMailService.createProjectMailAlias(projectId, { slug });
      }

      return this.loadAliasStatus(projectId);
    },

    async disableAlias(projectId: string) {
      await ProjectMailService.deleteProjectMailAlias(projectId);

      return this.loadAliasStatus(projectId);
    },

    /**
     * First page plus however many further pages the user had revealed — a refresh must
     * not collapse a list they expanded.
     *
     * Only page one on a cold load: the list is unbounded, so fetching all of it meant a
     * serial request per 100 mails before anything rendered.
     *
     * Cached per project since the sidebar re-mounts on every open; `force` bypasses it.
     */
    async loadMailItems(projectId: string, options?: { force?: boolean }) {
      if (!options?.force && this.items[projectId]) {
        return;
      }

      // Without this guard a second caller arriving mid-load — the tab watcher and a
      // socket refresh overlap routinely — starts an entire second run.
      const inFlight = loadsInFlight.get(projectId);
      if (inFlight) {
        return inFlight;
      }

      // Restore the user's expansion on a refresh; a cold load is one page.
      const pagesToLoad = Math.min(
        Math.max(1, this.loadedPages[projectId] ?? 1),
        MAX_PAGES,
      );

      const load = (async () => {
        const all: ProjectMailItemSummary[] = [];
        let total = 0;
        let page = 1;

        do {
          const result = await ProjectMailService.getProjectMailItems(
            projectId,
            page,
            PAGE_SIZE,
          );
          all.push(...result.items);
          total = result.total;
          page += 1;
          // Stop early when the server has fewer mails than we last had loaded,
          // which is what a delete or a purge looks like from here.
        } while (page <= pagesToLoad && all.length < total);

        this.items[projectId] = all;
        this.totals[projectId] = total;
        this.loadedPages[projectId] = page - 1;
      })();

      loadsInFlight.set(projectId, load);
      this.isLoading = true;

      try {
        await load;
      } finally {
        loadsInFlight.delete(projectId);
        this.isLoading = false;
      }
    },

    /** Append the next page. No-op once everything the server has is loaded. */
    async loadMoreMailItems(projectId: string) {
      if (this.isLoadingMore || !this.hasMoreMailItems(projectId)) {
        return;
      }

      // A refresh running concurrently is about to rewrite the whole list, so
      // appending onto it would duplicate or interleave rows.
      const inFlight = loadsInFlight.get(projectId);
      if (inFlight) {
        return inFlight;
      }

      const nextPage = (this.loadedPages[projectId] ?? 1) + 1;
      if (nextPage > MAX_PAGES) {
        return;
      }

      this.isLoadingMore = true;
      try {
        // Paged by the rows we hold, not by page index: a delete shifts every later row
        // up by one, so a fixed `page * limit` offset would step straight over the row
        // that moved into the previous window and never fetch it.
        const result = await ProjectMailService.getProjectMailItems(
          projectId,
          Math.floor((this.items[projectId] || []).length / PAGE_SIZE) + 1,
          PAGE_SIZE,
        );

        const known = new Set((this.items[projectId] || []).map((i) => i.id));
        // Mail arriving between two pages shifts every later row down by one, so the
        // same id can come back on the next page. Skip those rather than duplicate.
        const fresh = result.items.filter((item) => !known.has(item.id));

        this.items[projectId] = [...(this.items[projectId] || []), ...fresh];
        this.totals[projectId] = result.total;
        this.loadedPages[projectId] = nextPage;
      } finally {
        this.isLoadingMore = false;
      }
    },

    /**
     * One mail including its HTML body, which the list omits (a body can be 512 KB).
     * Fetched when a drag starts, so its text becomes the description of what the drop creates.
     */
    async loadMailItem(projectId: string, mailItemId: string) {
      return ProjectMailService.getProjectMailItem(projectId, mailItemId);
    },

    /** Soft-deletes the mail. Whatever was already created from it is untouched. */
    async deleteMailItem(projectId: string, mailItemId: string) {
      await ProjectMailService.deleteProjectMailItem(projectId, mailItemId);

      const before = (this.items[projectId] || []).length;
      this.items[projectId] = (this.items[projectId] || []).filter(
        (item) => item.id !== mailItemId,
      );

      // Keep the total in step, or the list would claim there is more to load and
      // then fetch a page that is entirely rows it already has.
      if (this.items[projectId].length < before && this.totals[projectId]) {
        this.totals[projectId] -= 1;
      }
    },
  },
});
